import { DynamicFormDelegate } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types/dynamicForms';
import { getCart } from 'api/operations/cartServiceV2';
import { getConfigByDate } from 'api/operations/domainServiceV2';
import { requestFormById } from 'api/operations/formService';
import { getEstimateCostAnswers, saveEstimateCostAnswers } from 'api/operations/proposalServiceV3';
import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { redirectToRoute } from 'routes/redirectToRoute';
import { RouteV2Id } from 'routes/routesConfigV2';
import { getRootStoreInstance } from 'store/rootStore';
import { Cart } from 'types/cartServiceV2';
import { FormBcByProductLine } from 'types/formService';
import { QuoteFlowDomainParams } from 'store/flows/quote/quoteFlowDelegate';

function toFormBrowsingCriteria(cart: Cart): FormBcByProductLine {
  const criteria = cart.lastUpdateBrowsingCriteria;
  const productLineIds = [...new Set(cart.cartItems.map((item) => item.productLineId))];

  return Object.fromEntries(
    productLineIds.map((productLineId) => [
      productLineId,
      [
        {
          effectiveDate: criteria.effectiveDate,
          zipCode: criteria.zipCode,
          state: criteria.state,
          county: criteria.county,
          ...(criteria.naics ? { naics: criteria.naics } : {}),
          ...(criteria.sic2 ? { sic2: criteria.sic2 } : {}),
          ...(criteria.sic4 ? { sic4: criteria.sic4 } : {}),
          ...(criteria.groupSize ? { groupSize: criteria.groupSize } : {}),
          ...criteria[productLineId],
        },
      ],
    ]),
  );
}

export default class EstimatedCostPageStore {
  private _domainData: QuoteFlowDomainParams;
  private _isInitialLoading = false;
  private _formId = '';
  private _savedAnswers: Record<string, string> = {};
  private _browsingCriteria: FormBcByProductLine = {};

  public dynamicJSONFormDelegate = runInAction(
    () =>
      new DynamicFormDelegate({
        currentUserRoles: getRootStoreInstance().userStore.userRoles,
        fetchForm: async (formId: string): Promise<FormObjectFromJSON> =>
          requestFormById(formId, this._browsingCriteria),
        onSubmit: this.saveAnswers.bind(this),
        disableResetAfterSubmit: true,
      }),
  );

  public get isInitialLoading(): boolean {
    return this._isInitialLoading;
  }

  private _setInitialLoading(isInitialLoading: boolean): void {
    this._isInitialLoading = isInitialLoading;
  }

  public async getInitialData(): Promise<void> {
    this._setInitialLoading(true);
    const [cart, answers] = await Promise.all([
      getCart(this._domainData.cartId),
      getEstimateCostAnswers(this._domainData.cartId),
    ]);
    const { userStore } = getRootStoreInstance();
    const config = await getConfigByDate(userStore.sponsorId, cart.lastUpdateBrowsingCriteria.effectiveDate);
    runInAction(() => {
      this._formId = config[cart.rateSegment]?.annualPremiumFormId || '';
      this._savedAnswers = answers;
      this._browsingCriteria = toFormBrowsingCriteria(cart);
      this._setInitialLoading(false);
    });
  }

  public async fetchForm(): Promise<void> {
    if (!this._formId) return;
    const answers = Object.keys(this._savedAnswers).length > 0 ? this._savedAnswers : undefined;
    await this.dynamicJSONFormDelegate.buildForm(this._formId, answers);
  }

  public async saveAnswers(): Promise<void> {
    this.dynamicJSONFormDelegate.setLoading(true);
    await saveEstimateCostAnswers(this._domainData.cartId, this.dynamicJSONFormDelegate.formAnswers);
    runInAction(() => {
      this.dynamicJSONFormDelegate.setLoading(false);
      this.returnBack();
    });
  }

  public returnBack(): void {
    redirectToRoute(RouteV2Id.INITIATE_SHELF_ADD_QUOTE, this._domainData);
  }

  constructor(domainData: QuoteFlowDomainParams) {
    this._domainData = domainData;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const EstimatedCostPageStoreContext = createContext(new EstimatedCostPageStore({} as QuoteFlowDomainParams));

export const useEstimatedCostPageStore = (): EstimatedCostPageStore => {
  return useContext(EstimatedCostPageStoreContext);
};

export { EstimatedCostPageStoreContext };
